import React, { useMemo, useRef, useState, useCallback } from 'react';
import { Field, FieldArray, Form, Formik } from 'formik';
import Modal from 'react-modal';

import Loader from '../loader/Loader';
import WorkoutBlock from './WorkoutBlock';
import CustomInput from '../input/CustomInput';
import CustomButton from '../button/CustomButton';
import BLOCKS from '../../constants/workoutBlocks';
import { CalendarType } from '../../constants/enums';
import CustomTextArea from '../input/CustomTextArea';
import EdittingWorkoutBlock from './EdittingWorkoutBlock';
import { CalendarSchema } from '../../schemas/program/coachProgram';

import { useUtils } from '../../hooks';

import {
  WholeWrapper,
  ModalHeader,
  ModalCloseX,
  ModalColumn,
  ModalHeaderCenterLine,
  LeftSideBlock,
  LeftSideBlockTitle,
  MessageCheck,
  MessageCheckBox,
  ContentWrapper,
  RightColumnContainer,
  WorkoutBlocksBox,
  DraggableSign,
  WorkoutBlocksContainer,
  PseudoWorkoutBlock,
  ModalFooter,
  ColumnsWrapper,
  ContentContainer,
  WorkoutBlockItem,
  LeftSideBlockOptional,
  WorkoutBlocksBoxScrollable,
  BeforeExitModal,
  BeforeExitModalContents,
} from './components';
import WorkoutDropbox from './WorkoutDropbox';
import useCalendar from '../../hooks/calendar';

const customStyles = {
  overlay: {
    backgroundColor: '#05000066',
    zIndex: 3333,
  },
  content: {
    width: '74%',
    maxWidth: '1320px',
    minWidth: '1080px',
    height: 'Calc(82vh + 30px)',
    top: '50%',
    left: '50%',
    right: 'auto',
    justifyContent: 'center',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    color: '#001212',
    borderRadius: '8px',
    border: 'none',
    padding: '0 ',
    pointerEvents: 'all',
  },
};

const AddCalendarModal = ({ day, closeModal, submitModal, loading, readOnly, coachProgramUid }) => {
  const [draggingBlock, setDraggingBlock] = useState(null);
  const [showBeforeExitModal, setShowBeforeExitModal] = useState(false);

  const formikRef = useRef();

  const { calendars, calendarsLoading } = useCalendar({
    coachProgramUid,
    startDate: day?.programDay,
    calendarUid: day?.uid,
  });

  const WORKOUT = useMemo(() => {
    if (Array.isArray(calendars)) {
      return calendars?.find((x) => x.type === 'WORKOUT');
    }

    return calendars;
  }, [calendars]);
  console.log(WORKOUT);
  const { getWorkoutBlocksByWorkout } = useUtils();

  const iniatialBlocks = useMemo(() => getWorkoutBlocksByWorkout(WORKOUT?.workout), [
    WORKOUT,
    getWorkoutBlocksByWorkout,
  ]);

  const initialValues = useMemo(
    () => ({
      name: WORKOUT ? WORKOUT?.workout?.name : `Day ${day?.programDay}`,
      beforeMessageChecked: !!WORKOUT?.workout?.beforeMessage,
      beforeMessage: WORKOUT ? WORKOUT?.workout?.beforeMessage : '',
      afterMessageChecked: !!WORKOUT?.workout?.afterMessage,
      afterMessage: WORKOUT ? WORKOUT?.workout?.afterMessage : '',
      type: CalendarType.WORKOUT,
      blocks: iniatialBlocks,
      editingBlockIndex: null,
    }),
    [day, WORKOUT, iniatialBlocks],
  );

  const hasUnsavedChanges = useCallback(() => {
    if (formikRef?.current) {
      let a = { ...formikRef.current.values };
      let b = { ...initialValues };

      a.blocks = a.blocks && a.blocks.length === 0 ? undefined : a.blocks;
      delete a.editingBlockIndex;
      delete b.editingBlockIndex;

      return JSON.stringify(a) !== JSON.stringify(b);
    } else {
      return false;
    }
  }, [initialValues, formikRef]);

  return (
    <Modal
      onRequestClose={() => {
        if (!readOnly && hasUnsavedChanges()) {
          setShowBeforeExitModal(true);
        } else {
          closeModal();
        }
      }}
      isOpen={!!day}
      style={customStyles}
      contentLabel="Program Request"
      ariaHideApp={false}
    >
      {loading || calendarsLoading ? (
        <Loader />
      ) : (
        <Formik
          innerRef={formikRef}
          initialValues={initialValues}
          enableReinitialize
          onSubmit={(v) => {
            submitModal(day?.programDay, v, WORKOUT?.workout?.uid);
            closeModal();
          }}
          validationSchema={CalendarSchema}
        >
          {({ setFieldValue, errors, validateForm, touched, values, setTouched }) => (
            <Form style={{ width: '100%', height: '100%' }}>
              <WholeWrapper>
                <ColumnsWrapper>
                  <ModalColumn left>
                    <ModalHeader>{`Day ${day?.programDay}`}</ModalHeader>
                    <LeftSideBlock>
                      <LeftSideBlockTitle>WORKOUT NAME</LeftSideBlockTitle>
                      <Field id="name" name="name">
                        {({ field }) => (
                          <CustomInput
                            outlined
                            height="4.4rem"
                            onChange={(v) => setFieldValue(field.name, v)}
                            value={field.value}
                            borderRadius="8px"
                            error={touched[field.name] && errors[field.name]}
                          />
                        )}
                      </Field>
                    </LeftSideBlock>
                    <LeftSideBlock scrollable>
                      <LeftSideBlockTitle>
                        SCHEDULE MESSAGE<LeftSideBlockOptional>(Optional)</LeftSideBlockOptional>
                      </LeftSideBlockTitle>
                      <Field id="beforeMessageChecked" name="beforeMessageChecked">
                        {({ field }) => (
                          <MessageCheck active={!!field.value}>
                            <MessageCheckBox
                              className="checkbox"
                              onClick={() => setFieldValue(field.name, !field.value)}
                            />
                            Before workout
                          </MessageCheck>
                        )}
                      </Field>
                      <Field id="beforeMessage" name="beforeMessage">
                        {({ field }) => (
                          <>
                            {!!values.beforeMessageChecked && (
                              <CustomTextArea
                                style={{
                                  height: '140px',
                                  fontFamily: 'Roboto-l',
                                  letterSpacing: '0.05rem',
                                }}
                                rows={null}
                                onChange={(v) => setFieldValue(field.name, v)}
                                value={field.value}
                              />
                            )}
                          </>
                        )}
                      </Field>
                      <Field id="afterMessageChecked" name="afterMessageChecked">
                        {({ field }) => (
                          <MessageCheck active={!!field.value}>
                            <MessageCheckBox
                              className="checkbox"
                              onClick={() => setFieldValue(field.name, !field.value)}
                            />
                            After workout
                          </MessageCheck>
                        )}
                      </Field>
                      <Field id="afterMessage" name="afterMessage">
                        {({ field }) => (
                          <>
                            {!!values.afterMessageChecked && (
                              <CustomTextArea
                                style={{
                                  height: '140px',
                                  fontFamily: 'Roboto-l',
                                  letterSpacing: '0.05rem',
                                }}
                                rows={null}
                                onChange={(v) => setFieldValue(field.name, v)}
                                value={field.value}
                              />
                            )}
                          </>
                        )}
                      </Field>
                    </LeftSideBlock>
                  </ModalColumn>
                  <ModalColumn main>
                    <ModalHeader>
                      <div />
                      <ModalHeaderCenterLine />
                      <div />
                    </ModalHeader>
                    <ContentWrapper>
                      <ContentContainer>
                        <FieldArray
                          name="blocks"
                          render={({ push, remove, insert, replace }) => (
                            <>
                              {values?.blocks?.map((block, i) =>
                                i === values?.editingBlockIndex ? (
                                  <EdittingWorkoutBlock
                                    block={block}
                                    key={i}
                                    index={i}
                                    setFieldValue={setFieldValue}
                                    validateForm={validateForm}
                                    errors={errors}
                                    touched={touched}
                                    setTouched={setTouched}
                                    deleteSelf={() => {
                                      remove(i);
                                      setFieldValue('editingBlockIndex', null);
                                    }}
                                  />
                                ) : (
                                  <WorkoutBlock
                                    block={block}
                                    key={i}
                                    deleteSelf={() => {
                                      remove(i);
                                      const beforeEditting = values.editingBlockIndex > i;
                                      if (beforeEditting && values.editingBlockIndex) {
                                        setFieldValue(
                                          'editingBlockIndex',
                                          values.editingBlockIndex - 1,
                                        );
                                      }
                                    }}
                                    duplicateSelf={(v) => {
                                      insert(i + 1, v);
                                    }}
                                    isFirst={i === 0}
                                    isLast={i === values?.blocks?.length - 1}
                                    orderUp={() => {
                                      const clone = block;
                                      if (i > 0) {
                                        const previousOne = values?.blocks?.[i - 1];
                                        replace(i, previousOne);
                                        replace(i - 1, clone);
                                        if (values?.editingBlockIndex === i - 1) {
                                          setFieldValue('editingBlockIndex', i);
                                        }
                                      }
                                    }}
                                    orderDown={() => {
                                      const clone = block;
                                      if (i < values?.blocks?.length - 1) {
                                        const nextOne = values?.blocks?.[i + 1];
                                        replace(i, nextOne);
                                        replace(i + 1, clone);
                                        if (values?.editingBlockIndex === i + 1) {
                                          setFieldValue('editingBlockIndex', i);
                                        }
                                      }
                                    }}
                                    setEditting={async () => {
                                      await setTouched({
                                        blocks: { [values.editingBlockIndex]: true },
                                      });
                                      const invalid = !!errors?.blocks?.[values.editingBlockIndex];
                                      if (!invalid) {
                                        setFieldValue('editingBlockIndex', i);
                                      }
                                    }}
                                  />
                                ),
                              )}
                              <WorkoutDropbox
                                draggingBlock={draggingBlock}
                                setDraggingBlock={setDraggingBlock}
                                push={push}
                                setFieldValue={setFieldValue}
                                errors={errors}
                                setTouched={setTouched}
                                touched={touched}
                                values={values}
                              />
                            </>
                          )}
                        ></FieldArray>
                      </ContentContainer>
                    </ContentWrapper>
                  </ModalColumn>
                  <ModalColumn right>
                    <ModalHeader>
                      <div />
                      <ModalCloseX
                        onClick={() => {
                          if (!readOnly && hasUnsavedChanges()) {
                            setShowBeforeExitModal(true);
                          } else {
                            closeModal();
                          }
                        }}
                      >
                        X
                      </ModalCloseX>
                      <BeforeExitModal className="beforeExitModal" show={showBeforeExitModal}>
                        <BeforeExitModalContents>
                          <span>You have unsaved changes. Do you want to save your changes?</span>
                          <CustomButton
                            style={{ margin: '0 auto', width: '70%', height: '4.8rem' }}
                            fontSize="1.4rem"
                            type="button"
                            red
                            onClick={() => {
                              setShowBeforeExitModal(false);
                              closeModal();
                            }}
                          >
                            No, thank you
                          </CustomButton>

                          <CustomButton
                            style={{ margin: '0 auto', width: '70%', height: '4.8rem' }}
                            fontSize="1.4rem"
                            type="submit"
                            green
                            onClick={() => setShowBeforeExitModal(false)}
                          >
                            Yes, please
                          </CustomButton>
                        </BeforeExitModalContents>
                      </BeforeExitModal>
                    </ModalHeader>
                    <RightColumnContainer>
                      <WorkoutBlocksBoxScrollable>
                        <WorkoutBlocksBox>
                          <DraggableSign /> DRAG A BLOCK
                          <WorkoutBlocksContainer>
                            {Object.keys(BLOCKS)?.map((key, i) => {
                              const block = BLOCKS[key];
                              return (
                                <WorkoutBlockItem
                                  draggable
                                  onDragStart={() => setDraggingBlock(block)}
                                  key={i}
                                  active={
                                    typeof values.editingBlockIndex === 'number' &&
                                    values.blocks[values.editingBlockIndex].type === block.type
                                  }
                                >
                                  {block.name}
                                </WorkoutBlockItem>
                              );
                            })}
                            <PseudoWorkoutBlock />
                          </WorkoutBlocksContainer>
                        </WorkoutBlocksBox>
                      </WorkoutBlocksBoxScrollable>
                    </RightColumnContainer>
                  </ModalColumn>
                </ColumnsWrapper>
                <ModalFooter>
                  {readOnly ? (
                    "This workout belongs to a global template. You can't change it"
                  ) : (
                    <CustomButton
                      style={{ margin: '0 1.2rem', width: '22.4rem', height: '4.8rem' }}
                      fontSize="1.4rem"
                      type="submit"
                      green
                    >
                      Save & Close
                    </CustomButton>
                  )}
                  {/* <CustomButton
                    style={{ margin: '0 1.2rem', width: '22.4rem', height: '4.8rem' }}
                    green
                    fontSize="1.4rem"
                    type="submit"
                  >
                    Save & Edit Next Day
                  </CustomButton> */}
                </ModalFooter>
              </WholeWrapper>
            </Form>
          )}
        </Formik>
      )}
    </Modal>
  );
};

export default AddCalendarModal;
